import { ChangeDetectionStrategy, Component, inject, computed, effect, signal } from '@angular/core';
import { IPermissionNode } from '../../interfaces';
import { MatTreeModule } from '@angular/material/tree';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDividerModule } from '@angular/material/divider';
import { RolesManagementStore } from '../../store/roles-management.store';
import { ArrayDataSource } from '@angular/cdk/collections';
import { CommonModule } from '@angular/common';
import { AppStore } from '../../../../app.store';
import { ClientPlatform } from '../../enum';
import { TranslationService } from '../../../../shared/services/translation.service';
import { getTranslatedDesktopPermissions, getTranslatedMobilePermissions, permissionToNodes } from '../../roles-management.helper';
import { TranslatePipe } from '../../../../shared/pipes/translationPipe';

@Component({
  selector: 'app-permissions',
  imports: [
    CommonModule,
    MatTreeModule,
    MatSlideToggleModule,
    MatCheckboxModule,
    MatDividerModule,
    TranslatePipe
],
  templateUrl: './permissions.component.html',
  styleUrl: './permissions.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PermissionsComponent {
  readonly store = inject(RolesManagementStore);
  readonly appStore = inject(AppStore);
  private readonly translationService = inject(TranslationService);
  readonly ClientPlatform = ClientPlatform;

  childrenAccessor = (node: IPermissionNode) => node.children ?? [];
  hasChild = (_: number, node: IPermissionNode) =>
    !!node.children && node.children.length > 0;
  trackBy = (_: number, n: IPermissionNode) => n.resource;

  private translatedDesktopPermissions = signal<IPermissionNode[]>([]);
  private translatedMobilePermissions = signal<IPermissionNode[]>([]);

  readonly translatedDesktopTree = computed(() => {
    const tempRole = this.store.tempRole();
    const translatedPermissions = this.translatedDesktopPermissions();
    if (!tempRole || !translatedPermissions.length) return [];
    return permissionToNodes(
      tempRole.permissions.desktop ?? [],
      translatedPermissions,
    );
  });

  readonly translatedMobileTree = computed(() => {
    const tempRole = this.store.tempRole();
    const translatedPermissions = this.translatedMobilePermissions();
    if (!tempRole || !translatedPermissions.length) return [];
    return permissionToNodes(
      tempRole.permissions.mobile ?? [],
      translatedPermissions,
    );
  });

  constructor() {
    getTranslatedDesktopPermissions(this.translationService).subscribe(permissions => {
      this.translatedDesktopPermissions.set(permissions);
    });

    getTranslatedMobilePermissions(this.translationService).subscribe(permissions => {
      this.translatedMobilePermissions.set(permissions);
    });

    effect(() => {
      this.translationService.getCurrentLanguage$().subscribe(() => {
        getTranslatedDesktopPermissions(this.translationService).subscribe(permissions => {
          this.translatedDesktopPermissions.set(permissions);
        });

        getTranslatedMobilePermissions(this.translationService).subscribe(permissions => {
          this.translatedMobilePermissions.set(permissions);
        });
      });
    });
  }

  getDataSource(data: IPermissionNode): ArrayDataSource<IPermissionNode> {
    return new ArrayDataSource([data]);
  }
}
